// Admin tab: sync history, "Sync now", and who can do what. Data is fetched on demand and cached; redraws never fetch.
import { fmtDMY, TZ } from '@shared/index';
import type { SyncRun, UserRow } from '../backend/types';
import { getBackend } from '../actions';
import { currentTab } from '../nav';
import { registerView } from '../render';
import { store } from '../store';
import { $, el, td, toast } from '../ui';

const REFRESH_MS = 30_000;
const RESULT: Record<SyncRun['status'], string> = { ok: 'Ok', aborted: 'Aborted', failed: 'Failed', running: 'Running' };
const ROLES: Array<[string, string]> = [['admin', 'Admin'], ['pm', 'PM'], ['viewer', 'Viewer']];

let runs: SyncRun[] = [];
let users: UserRow[] = [];
let timer: ReturnType<typeof setInterval> | undefined;
let syncing = false;

const onAdmin = () => currentTab() === 'admin';

function when(ts: string): string {
  const time = new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ });
  return fmtDMY(ts) + ' ' + time;
}

function details(r: SyncRun): HTMLElement | string {
  const hasDetails = r.details && Object.keys(r.details).length > 0;
  if (!r.errors?.length && !hasDetails) return '';
  const d = el('details');
  d.append(el('summary', undefined, 'View'));
  const text = [...(r.errors || []), ...(hasDetails ? [JSON.stringify(r.details, null, 2)] : [])].join('\n');
  d.append(el('pre', undefined, text));
  return d;
}

function runRow(r: SyncRun): HTMLTableRowElement {
  const tr = el('tr');
  tr.append(td(when(r.started_at), 'l'), td(r.trigger, 'l'), td(RESULT[r.status] ?? r.status, 'l st-' + r.status));
  [r.rows_read, r.added, r.updated, r.completed, r.deleted].forEach((n) => tr.append(td(n)));
  const cell = el('td', 'l');
  cell.append(details(r));
  tr.append(cell);
  return tr;
}

function select(options: Array<[string, string]>, value: string, label: string, onPick: (v: string) => void): HTMLSelectElement {
  const s = el('select');
  s.setAttribute('aria-label', label);
  options.forEach(([v, text]) => { const o = el('option', undefined, text); o.value = v; s.append(o); });
  s.value = value;
  s.addEventListener('change', () => onPick(s.value));
  return s;
}

function userRow(u: UserRow): HTMLTableRowElement {
  const tr = el('tr', 'adm-row');
  const pmOptions: Array<[string, string]> = [['', 'None'], ...store.pms.filter((p) => p.active).map((p): [string, string] => [p.name, p.name])];
  if (u.pm_name && !pmOptions.some(([v]) => v === u.pm_name)) pmOptions.push([u.pm_name, u.pm_name]);
  const role = el('td', 'l'), pm = el('td', 'l');
  role.append(select(ROLES, u.role, 'Role for ' + u.email, (v) => saveUser(u.id, { role: v })));
  pm.append(select(pmOptions, u.pm_name ?? '', 'PM name for ' + u.email, (v) => saveUser(u.id, { pm_name: v || null })));
  tr.append(td(u.email, 'l'), role, pm, td(fmtDMY(u.created_at), 'l'));
  return tr;
}

function draw() {
  const rb = $('runs');
  rb.replaceChildren();
  if (!runs.length) {
    const tr = el('tr'), c = td('No sync runs yet.', 'l');
    c.colSpan = 9;
    tr.append(c);
    rb.append(tr);
  } else runs.forEach((r) => rb.append(runRow(r)));
  // Don't rebuild the user selects while one is open: it would close the dropdown.
  if (!document.activeElement?.closest('#users')) $('users').replaceChildren(...users.map(userRow));
}

async function loadRuns() {
  try { runs = await getBackend().listSyncRuns(50); draw(); } catch (e) { console.error(e); }
}

async function loadUsers() {
  try { users = await getBackend().listUsers(); draw(); } catch (e) { console.error(e); }
}

async function saveUser(id: string, patch: { role?: string; pm_name?: string | null }) {
  try {
    await getBackend().updateUser(id, patch);
    toast('Saved');
  } catch (e) {
    toast(e instanceof Error && e.message ? e.message : 'Couldn’t save. Try again.');
  }
  (document.activeElement as HTMLElement | null)?.blur();
  await loadUsers();
}

async function syncNow() {
  if (syncing) return;
  syncing = true;
  const btn = $<HTMLButtonElement>('syncNow'), note = $('syncNote');
  btn.disabled = true;
  note.textContent = 'Syncing…';
  try {
    const r = await getBackend().syncNow();
    toast(r.message);
    note.textContent = r.message;
  } catch (e) {
    const msg = e instanceof Error && e.message ? e.message : 'Sync failed.';
    toast(msg);
    note.textContent = msg;
  } finally {
    syncing = false;
    btn.disabled = false;
  }
  await loadRuns();
}

/** Called on every hash change: load when the tab opens, poll while it's open, stop when it isn't. */
function onShow() {
  if (!onAdmin()) { clearInterval(timer); timer = undefined; return; }
  if (timer) return;
  void loadRuns();
  void loadUsers();
  timer = setInterval(() => { if (onAdmin()) void loadRuns(); }, REFRESH_MS);
}

export function initAdmin() {
  // The first render after sign-in also covers a page opened straight on #admin.
  registerView('admin', () => { draw(); if (onAdmin() && !timer) onShow(); });
  $('syncNow').addEventListener('click', () => void syncNow());
  window.addEventListener('hashchange', onShow);
  onShow();
}
